import React from 'react';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import { orderBy } from 'natural-orderby';
import { HorizontalFlex } from 'smartsuite-ui';
import {
    allMetricSourcesAuthorized,
    displayMetricSourceError,
    getMetricSourceErrors,
    getMetricSourceForRequest,
    hasMetricSources,
} from '../../utils/wrapperUtils';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import { getFormattedTimeFromMilliseconds } from '../../utils/dataFormatUtils';
import StatusCellsGroup from './StatusCellsGroup';
import StatusCellsLegend from './StatusCellsLegend';

// eslint-disable-next-line complexity
function getMetricSource({ metricRequests, metrics, thresholds }) {
    if (
        metricRequests.length === 0 ||
        metricRequests[0].metrics.length === 0 ||
        metricRequests[0].groupBy.length === 0 ||
        metricRequests[0].groupBy.length > 2
    ) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    if (!thresholds.length) {
        return { errorKey: 'invalidThresholdConfig' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

// eslint-disable-next-line complexity
export default function StatusCellsWrapper(props) {
    const {
        isTime,
        numberOfColumns,
        timeFormat,
        suffix = '',
    } = props.configuration;

    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const metric = metricRequest.metrics[0];

    const groups = groupBy(metricSource.metricSeries, metricRequest.groupBy[0]);

    const formatter = isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const sortedThresholds = orderBy(props.thresholds, 'value');
    const groupKeys = orderBy(Object.keys(groups));

    function renderStatusCellsGroups() {
        if (metricRequest.groupBy.length > 1) {
            return groupKeys.map((groupKey) => (
                <StatusCellsGroup
                    key={groupKey}
                    dataFormat={timeFormat}
                    dataFormatter={formatter}
                    groupLabel={groupKey}
                    groupBy={metricRequest.groupBy[1]}
                    dataPoints={groups[groupKey]}
                    metric={metric}
                    numberOfColumns={numberOfColumns}
                    suffix={suffix}
                    thresholds={sortedThresholds}
                />
            ));
        }

        return (
            <StatusCellsGroup
                dataFormat={timeFormat}
                dataFormatter={formatter}
                groupBy={metricRequest.groupBy[0]}
                dataPoints={metricSource.metricSeries}
                metric={metric}
                numberOfColumns={numberOfColumns}
                suffix={suffix}
                thresholds={sortedThresholds}
            />
        );
    }

    return (
        <div>
            <StatusCellsLegend thresholds={sortedThresholds} />
            <HorizontalFlex
                cssClassModifiers={['flex-wrap', 'align-flex-start']}
            >
                {renderStatusCellsGroups()}
            </HorizontalFlex>
        </div>
    );
}

StatusCellsWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
